/**
 * Whether a click landed outside the current engagement card and its
 * associated popovers (saved replies, emoji picker, and any portaled popover
 * such as the private-account notice). Engagement-agnostic: the caller resolves
 * the current card element (Comments via `[data-comment-id]`, Mentions via
 * `[data-mention-id]`) and passes it in.
 */
const isClickOutsideEngagementCard = (
  event: MouseEvent,
  savedRepliesElement: HTMLElement | null,
  emojiPickerElement: HTMLElement | null,
  cardElement: HTMLElement | null,
): boolean => {
  if (!(event.target instanceof Node)) return false
  const target = event.target

  // Inside the saved-replies popover.
  if (savedRepliesElement && savedRepliesElement.contains(target)) return false

  // Inside the emoji picker.
  if (emojiPickerElement && emojiPickerElement.contains(target)) return false

  // Inside a portaled popover rendered outside the card wrapper.
  if (target instanceof Element) {
    const privateAccountPopover = target.closest(
      '[data-private-account-popover="true"]',
    )
    if (privateAccountPopover) return false
  }

  // Inside the current card's own content.
  if (cardElement && cardElement.contains(target)) return false

  return true
}

/**
 * Possible actions when clicking outside an engagement reply form. The two
 * `discard` variants are part of the contract but unreachable today (the
 * resolver only ever collapses); kept so callers can switch exhaustively.
 */
export type EngagementClickOutsideAction =
  | { type: 'none' }
  | { type: 'collapse' }
  | { type: 'discard' }
  | { type: 'discard-and-collapse' }

/**
 * Decides what an outside click should do, given the form's state and the DOM.
 * The caller supplies the resolved current-card element and a selector that
 * matches *any* engagement card wrapper, so the same logic serves both comment
 * and mention cards regardless of their differing data attributes.
 *
 * @see EngagementClickOutsideAction
 */
export const determineEngagementClickOutsideAction = (
  event: MouseEvent,
  options: {
    expanded: boolean
    status: string
    /** The current card's root element, or null if not in the DOM. */
    currentCardElement: HTMLElement | null
    /** Selector matching any engagement card wrapper. */
    cardWrapperSelector: string
    savedRepliesRef: HTMLElement | null
    emojiPickerRef: HTMLElement | null
  },
): EngagementClickOutsideAction => {
  const {
    expanded,
    status,
    currentCardElement,
    cardWrapperSelector,
    savedRepliesRef,
    emojiPickerRef,
  } = options

  // Only act on an expanded form.
  if (!expanded) {
    return { type: 'none' }
  }

  // Don't collapse while a dialog is open (e.g. the post-details dialog) — the
  // click may be on the dialog overlay or its close button.
  const openDialog = document.querySelector('[role="dialog"]')
  if (openDialog) {
    return { type: 'none' }
  }

  const target = event.target

  const clickedOutsideForm = isClickOutsideEngagementCard(
    event,
    savedRepliesRef,
    emojiPickerRef,
    currentCardElement,
  )

  if (!clickedOutsideForm) {
    return { type: 'none' }
  }

  // Clicking on any other engagement card: leave it to that card's own click
  // handler (which expands it, collapsing this one).
  const clickedAnotherCard =
    target instanceof Element ? target.closest(cardWrapperSelector) : null
  if (clickedAnotherCard) {
    return { type: 'none' }
  }

  // Mid-send or just-sent: collapse but never discard (the reply is committed).
  if (status === 'processing' || status === 'published') {
    return { type: 'collapse' }
  }

  // Clicking empty space outside all cards — collapse (the draft is preserved).
  return { type: 'collapse' }
}
